import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useMaterialSubcategoryApi from "hooks/api/private/useMaterialSubcategoryApi";
import useSnackbar from "hooks/useSnackbar";

import { MaterialSubcategoryListItem } from "types/api/materialSubcategory";

export type MaterialSubcategoryListHook = {
    navigateToNewMaterialSubcategory: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    dataTableHook: DataTableHook<MaterialSubcategoryListItem>;
    alertConfirmHook: AlertConfirmHookType;
};

function useMaterialSubcategoryList(): MaterialSubcategoryListHook {
    const navigate = useNavigate();

    const { getAllMaterialSubcategory, deleteMaterialSubcategory } = useMaterialSubcategoryApi();

    const onEditClick = React.useCallback(
        (row: MaterialSubcategoryListItem) => {
            navigate(makeUrl(":materialSubcategoryId/modifica", { materialSubcategoryId: row.id }));
        },
        [navigate]
    );

    const navigateToNewMaterialSubcategory = React.useCallback(() => {
        navigate("nuova");
    }, [navigate]);

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "material_subcategory_languages.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        []
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "material-subcategory-list-search",
        memoizedFieldsForSearch
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.search.name = "" + event.target.value;

                return newForm;
            }),
        [setFiltersForm]
    );

    const { filters } = filterSearchHook;

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Sottocategoria",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteMaterialSubcategoryButton = React.useCallback(
        (row: MaterialSubcategoryListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare la sottocategoria?", () => {
                const [apiCall] = deleteMaterialSubcategory({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Sottocategoria eliminata con successo");
                    })
                    .catch(() =>
                        enqueueErrorSnackbar("Errore nell'eliminazione della sottocategoria")
                    );
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteMaterialSubcategory,
        ]
    );

    const columns: Array<Column<MaterialSubcategoryListItem>> = React.useMemo(
        () => [
            {
                id: "name",
                header: "Nome",
                render: (row: MaterialSubcategoryListItem) => row.name,
                orderable: true,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: MaterialSubcategoryListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={onEditClick}
                        onDelete={deleteMaterialSubcategoryButton}
                    />
                ),
            },
        ],
        [onEditClick, deleteMaterialSubcategoryButton]
    );

    const dataTableHook = useDataTableWithMemory<MaterialSubcategoryListItem>({
        apiCall: getAllMaterialSubcategory,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "material-subcategory-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();
            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    return React.useMemo(
        (): MaterialSubcategoryListHook => ({
            navigateToNewMaterialSubcategory,
            onSearchFilterChange,
            filterSearchHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            navigateToNewMaterialSubcategory,
            onSearchFilterChange,
            filterSearchHook,
            alertConfirmHook,
            dataTableHook,
        ]
    );
}

export default useMaterialSubcategoryList;
